import type { ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  /** Footer actions (buttons), right-aligned. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizes = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
} as const;

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  className,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-secondary-950/50 backdrop-blur-sm data-[state=open]:animate-fade-in" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
            // ⚠ Never taller than the screen. The Edit person form outgrew a
            // 900px window once addresses were added and its Save button sat
            // below the fold, unreachable: the title and the buttons stay put
            // and the BODY scrolls.
            'flex max-h-[calc(100dvh-2rem)] flex-col',
            'card animate-scale-in p-6 shadow-elevated focus:outline-none',
            sizes[size],
            className,
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-heading-3 text-secondary">{title}</Dialog.Title>
              {description && (
                <Dialog.Description className="mt-1 text-body-sm text-grey-500">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close
              className="rounded-lg p-1.5 text-grey-400 transition-colors hover:bg-grey-100 hover:text-secondary focus:outline-none focus:ring-2 focus:ring-grey-300"
              aria-label="Close"
            >
              <X className="h-5 w-5" aria-hidden />
            </Dialog.Close>
          </div>
          {/* The padding lives inside the scroller so focus rings at its edges
              are not clipped. */}
          <div className="-mx-6 mt-3 min-h-0 flex-1 overflow-y-auto px-6 py-1">{children}</div>
          {footer && <div className="mt-5 flex shrink-0 justify-end gap-2">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
